<script setup lang="ts">
/**
 * „Anna schreibt …“ / „denkt nach …“: three pulsing dots and a label, for a
 * chat whose peers are typing or an assistant that is still answering.
 *
 * `names` builds the German sentence (see `formatTypingNames`; pass
 * `formatNames` for another language), `label` sets the text directly and wins.
 * Without either — or with `active` false — nothing shows, but the polite live
 * region stays mounted, so screen readers hear the label when it appears
 * instead of missing a region that was inserted together with its text.
 *
 * `variant="bubble"` draws the dots in a message bubble (the assistant's
 * pending answer); `inline` (default) is a quiet line under a thread. With
 * reduced motion the dots stand still.
 */
import { computed } from 'vue';
import { formatTypingNames } from './typingLabel';

export type NxTypingIndicatorVariant = 'inline' | 'bubble';

const props = withDefaults(
  defineProps<{
    /** Who is typing; builds the label. */
    names?: readonly string[];
    /** Text instead of the one built from `names` („denkt nach …“). */
    label?: string;
    /** Force it on or off; default: on while there is a label. */
    active?: boolean;
    variant?: NxTypingIndicatorVariant;
    /** Builds the label from the non-blank `names` (default: German); never called with none. */
    formatNames?: (names: readonly string[]) => string;
  }>(),
  {
    names: () => [],
    label: undefined,
    active: undefined,
    variant: 'inline',
    formatNames: formatTypingNames,
  }
);

const text = computed(() => {
  const label = props.label?.trim();
  if (label) return label;
  const names = props.names.map((n) => n.trim()).filter(Boolean);
  return names.length ? props.formatNames(names) : '';
});
/** `active` without a label shows the dots alone. */
const visible = computed(() => props.active ?? !!text.value);
</script>

<template>
  <div
    class="nx-typing-indicator"
    :class="[`is-${variant}`, { 'is-visible': visible }]"
    role="status"
    aria-live="polite"
    data-testid="nx-typing-indicator"
  >
    <span v-if="visible" class="nx-typing-indicator__content">
      <span class="nx-typing-indicator__dots" aria-hidden="true">
        <span class="nx-typing-indicator__dot" />
        <span class="nx-typing-indicator__dot" />
        <span class="nx-typing-indicator__dot" />
      </span>
      <span v-if="text" class="nx-typing-indicator__label">{{ text }}</span>
    </span>
  </div>
</template>

<style scoped>
.nx-typing-indicator {
  display: flex;
  align-items: center;
  min-width: 0;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
}
.nx-typing-indicator__content {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  min-width: 0;
  animation: nx-typing-in var(--nxt-motion-base) var(--nxt-ease-out) both;
}
.nx-typing-indicator.is-bubble .nx-typing-indicator__content {
  padding: calc(var(--nxt-space-xs) * 1.25) var(--nxt-space-sm);
  border: var(--nxt-hairline) solid var(--nxt-border-subtle);
  border-radius: var(--nxt-radius-lg);
  border-end-start-radius: var(--nxt-radius-sm);
  background: var(--nxt-surface-sunken-bg);
}
.nx-typing-indicator__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-style: italic;
}
.nx-typing-indicator__dots {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: calc(var(--nxt-space-2xs) * 0.75);
  height: var(--nxt-icon-size-sm);
}
.nx-typing-indicator__dot {
  width: calc(var(--nxt-size-unit) * 0.3125);
  height: calc(var(--nxt-size-unit) * 0.3125);
  border-radius: var(--nxt-radius-full);
  background: currentColor;
  animation: nx-typing-dot 1.2s var(--nxt-ease-in-out) infinite both;
}
.nx-typing-indicator.is-bubble .nx-typing-indicator__dot {
  background: var(--nxt-brand-bg);
}
.nx-typing-indicator__dot:nth-child(2) {
  animation-delay: 0.16s;
}
.nx-typing-indicator__dot:nth-child(3) {
  animation-delay: 0.32s;
}

@keyframes nx-typing-dot {
  0%,
  70%,
  100% {
    opacity: 0.35;
    transform: translateY(0) scale(0.8);
  }
  35% {
    opacity: 1;
    transform: translateY(calc(var(--nxt-size-unit) * -0.1875)) scale(1);
  }
}
@keyframes nx-typing-in {
  from {
    opacity: 0;
    transform: translateY(calc(var(--nxt-size-unit) * 0.25));
  }
}

@media (prefers-reduced-motion: reduce) {
  .nx-typing-indicator__content,
  .nx-typing-indicator__dot {
    animation: none;
  }
  .nx-typing-indicator__dot {
    opacity: 0.6;
  }
}
/* The same where a subtree asks for less motion: data-nx-motion="reduced"
   on an ancestor (reset.css does the durations). */
:where([data-nx-motion='reduced']) .nx-typing-indicator__content,
:where([data-nx-motion='reduced']) .nx-typing-indicator__dot {
  animation: none;
}
:where([data-nx-motion='reduced']) .nx-typing-indicator__dot {
  opacity: 0.6;
}
</style>
